import React, { createContext, useMemo, useState } from 'react';

import { ExcludeMethods } from '@types';

export interface IDeepLinkingContext {
  /**
   * URL by which the app was opened from terminated state
   */
  initialUrl: string | null;
  /**
   * URL by which the app was opened from background state
   */
  openedUrl: string | null;
  /**
   * This flag helps us prevent unnecessary deepLinking navigation
   * during the user authorization
   */
  hasEverBeenAuthorized: boolean;
  setInitialUrl(value: string | null): void;
  setOpenedUrl(value: string | null): void;
  clear(): void;
  updateHasEverBeenAuthorized(): void;
}

export const DeepLinkingContext = createContext<IDeepLinkingContext>({
  initialUrl: null,
  openedUrl: null,
  hasEverBeenAuthorized: false,
  setInitialUrl: () => {},
  setOpenedUrl: () => {},
  clear: () => {},
  updateHasEverBeenAuthorized: () => {},
});

type StateType = ExcludeMethods<IDeepLinkingContext>;

interface IDeepLinkingProviderProps {
  children: React.ReactNode;
}

export function DeepLinkingProvider({
  children,
}: IDeepLinkingProviderProps) {
  const [state, setState] = useState<StateType>({
    initialUrl: null,
    openedUrl: null,
    hasEverBeenAuthorized: false,
  });

  function setInitialUrl(url: string) {
    setState((v) => ({ ...v, initialUrl: url }));
  }

  function setOpenedUrl(url: string) {
    setState((v) => ({ ...v, openedUrl: url }));
  }

  function clear() {
    setState((v) => ({ ...v, openedUrl: null, initialUrl: null }));
  }

  function updateHasEverBeenAuthorized() {
    setState((v) => ({ ...v, hasEverBeenAuthorized: true }));
  }

  const contextValue = useMemo(
    () => ({
      ...state,
      setInitialUrl,
      setOpenedUrl,
      clear,
      updateHasEverBeenAuthorized,
    }),
    [state],
  );

  return (
    <DeepLinkingContext.Provider value={contextValue}>
      {children}
    </DeepLinkingContext.Provider>
  );
}
